/**
 * SteampunkCarousel — unified Swiper.js wrapper with coverflow effect.
 *
 * Carousel pattern inspired by Skiper UI (Skiper49) — https://skiper-ui.com/v1/skiper49
 * Built with Swiper.js — https://swiperjs.com/ — MIT License.
 *
 * All carousels in the app flow through this component.
 * Handles: touch, mouse, keyboard, pagination, virtual slides, empty state.
 */
import { useRef, useEffect, useMemo, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  EffectCoverflow,
  Pagination,
  Navigation,
  Keyboard,
  Autoplay,
  Virtual,
} from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface SteampunkCarouselProps<T> {
  items: T[];
  renderSlide: (item: T, index: number) => ReactNode;
  keyExtractor: (item: T) => string;
  loop?: boolean;
  autoplay?: boolean;
  showPagination?: boolean;
  showNavigation?: boolean;
  spaceBetween?: number;
  slidesPerView?: number | "auto";
  activeIndex?: number;
  onSlideChange?: (index: number) => void;
  onNearEnd?: () => void;
  emptyIcon?: ReactNode;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyAction?: ReactNode;
  virtual?: boolean;
  className?: string;
  testId?: string;
  ariaLabel?: string;
}

const NEAR_END_THRESHOLD = 5;

export function SteampunkCarousel<T>({
  items,
  renderSlide,
  keyExtractor,
  loop = true,
  autoplay: autoplayEnabled = false,
  showPagination = true,
  showNavigation = false,
  spaceBetween = 0,
  slidesPerView = "auto",
  activeIndex,
  onSlideChange,
  onNearEnd,
  emptyIcon,
  emptyTitle,
  emptySubtitle,
  emptyAction,
  virtual: virtualProp,
  className,
  testId,
  ariaLabel,
}: SteampunkCarouselProps<T>) {
  const swiperRef = useRef<SwiperType | null>(null);
  const useVirtual = virtualProp ?? items.length > 30;
  // Swiper: loop and virtual are mutually incompatible
  const useLoop = loop && items.length > 2 && !useVirtual;

  const modules = useMemo(
    () => [
      EffectCoverflow,
      Keyboard,
      ...(showPagination ? [Pagination] : []),
      ...(showNavigation ? [Navigation] : []),
      ...(autoplayEnabled ? [Autoplay] : []),
      ...(useVirtual ? [Virtual] : []),
    ],
    [showPagination, showNavigation, autoplayEnabled, useVirtual],
  );

  // Sync controlled activeIndex — skip if Swiper is mid-animation to avoid flutter
  useEffect(() => {
    const s = swiperRef.current;
    if (activeIndex == null || !s || s.destroyed) return;
    if (s.animating) return;
    if (s.realIndex !== activeIndex) {
      s.slideTo(activeIndex);
    }
  }, [activeIndex]);

  if (items.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        style={{ aspectRatio: "3/4", maxHeight: 400 }}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        {emptyIcon && <div className="mb-4 text-4xl">{emptyIcon}</div>}
        {emptyTitle && (
          <p className="font-serif text-lg font-bold text-foreground">{emptyTitle}</p>
        )}
        {emptySubtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{emptySubtitle}</p>
        )}
        {emptyAction && <div className="mt-4">{emptyAction}</div>}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{ duration: 0.3, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      className={className}
      data-testid={testId}
    >
      <Swiper
        onSwiper={(s) => {
          swiperRef.current = s;
        }}
        modules={modules}
        effect="coverflow"
        grabCursor
        centeredSlides
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        loop={useLoop}
        keyboard={{ enabled: true }}
        autoplay={
          autoplayEnabled
            ? { delay: 3000, disableOnInteraction: true }
            : false
        }
        pagination={showPagination ? { clickable: true } : false}
        navigation={showNavigation}
        virtual={
          useVirtual
            ? { addSlidesBefore: 2, addSlidesAfter: 2 }
            : undefined
        }
        coverflowEffect={{
          rotate: 40,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: true,
        }}
        preventClicks
        preventClicksPropagation
        threshold={10}
        onSlideChange={(s) => {
          onSlideChange?.(s.realIndex);
          if (onNearEnd && s.realIndex >= items.length - NEAR_END_THRESHOLD) {
            onNearEnd();
          }
        }}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
        style={{ paddingBottom: showPagination ? 40 : 0 }}
      >
        {items.map((item, i) => (
          <SwiperSlide
            key={keyExtractor(item)}
            style={{ width: "min(300px, 85vw)" }}
            virtualIndex={useVirtual ? i : undefined}
          >
            {renderSlide(item, i)}
          </SwiperSlide>
        ))}
      </Swiper>
    </motion.div>
  );
}
